import { getAppConfig } from '../../../common/config/app-config';
import { APP_CONSTANTS } from '../../../common/config/app-constants';
import { httpUpload } from '../../../common/services/ajax-services';

const { encodedContextPath } = getAppConfig();

export interface MultiUploadResponse {
  imageUrls: string[];
}

function isValidResponse(data: unknown): data is MultiUploadResponse {
  return typeof data === 'object' && data !== null && Array.isArray((data as any).imageUrls);
}

export const handleImageUploadCommon = (
  event: Event,
  currentImages: File[],
  currentPreviews: string[],
  maxImages: number,
  options?: {
    maxFileSizeMB?: number;
    allowedTypes?: string[];
  }
): { images: File[]; previews: string[]; error?: string; isFileTooLarge?: boolean } => {
  const input = event.target as HTMLInputElement;

  if (!input.files) {
    return { images: currentImages, previews: currentPreviews };
  }

  const selectedFiles = Array.from(input.files);
  const remainingSlots = maxImages - currentImages.length;
  const filesToAdd = selectedFiles.slice(0, remainingSlots);

  const newImages = [...currentImages];
  const newPreviews = [...currentPreviews];

  for (const file of filesToAdd) {
    if (options?.allowedTypes && !options.allowedTypes.includes(file.type)) {
      return {
        images: currentImages,
        previews: currentPreviews,
        error: 'Invalid file type',
      };
    }

    if (options?.maxFileSizeMB && file.size > options.maxFileSizeMB * 1024 * 1024) {
      return {
        images: currentImages,
        previews: currentPreviews,
        error: 'File too large',
        isFileTooLarge: true,
      };
    }

    newImages.push(file);
    newPreviews.push(URL.createObjectURL(file));
  }

  input.value = '';

  return {
    images: newImages,
    previews: newPreviews,
  };
};

export const removeImageCommon = (
  index: number,
  images: File[],
  previews: string[]
): { images: File[]; previews: string[] } => {
  URL.revokeObjectURL(previews[index]);

  const newImages = images.filter((_, i) => i !== index);
  const newPreviews = previews.filter((_, i) => i !== index);

  return {
    images: newImages,
    previews: newPreviews,
  };
};

export async function uploadImages(files: File[]): Promise<string[]> {
  if (!files.length) return [];

  const formData = new FormData();

  files.forEach(file => {
    formData.append(`files`, file);
  });

  const url = `${encodedContextPath}/${APP_CONSTANTS.UPLOAD_IMAGE}`;

  const response = await httpUpload(url, formData);

  if (!isValidResponse(response)) {
    throw new Error('Invalid upload response');
  }

  return response.imageUrls;
}
